import { Fragment, useEffect, useRef, useState } from "react";
import { getTempos } from "../../api/tempo.api";

function fmtHoras(h) {
  if (h == null || h === 0) return "—";
  const total = Math.round(h * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${hh}:${String(mm).padStart(2, "0")}`;
}

function fmtDia(iso) {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

function fmtDiaSemana(iso) {
  const dt = new Date(`${iso}T00:00:00`);
  return ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"][dt.getDay()];
}

function isFimDeSemana(iso) {
  const dow = new Date(`${iso}T00:00:00`).getDay();
  return dow === 0 || dow === 6;
}

function fmtDataBR(iso) {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const DEV_AVATAR_MAP = [
  { match: "guilherme", bg: "#712B13", color: "#F5C4B3" },
  { match: "paulo",     bg: "#0C447C", color: "#B5D4F4" },
  { match: "marcelo",   bg: "#085041", color: "#9FE1CB" },
  { match: "rudi",      bg: "#3C3489", color: "#CECBF6" },
  { match: "nelson",    bg: "#1C5040", color: "#A0DFC4" },
];
function devAv(nome) {
  if (!nome) return { bg: "#2C2C2A", color: "#B4B2A9" };
  const l = nome.toLowerCase();
  for (const m of DEV_AVATAR_MAP) if (l.includes(m.match)) return m;
  return { bg: "#2C2C2A", color: "#B4B2A9" };
}
function devInitials(nome) {
  if (!nome) return "?";
  const p = nome.trim().split(/\s+/).filter(Boolean);
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : p[0][0].toUpperCase();
}

// Uma tarefa sem sprint vinculada é tratada como "ativa" (não dá pra saber
// se é retrabalho sem essa informação, então não penaliza).
function isTarefaSprintAtiva(t, sprintAtivo) {
  return !t.sprint || t.sprint === sprintAtivo;
}

// Agrupa as tarefas de um dev por sprint (fixed_version_id, via t.sprint).
// O grupo da sprint ativa vem primeiro; os demais mantêm a ordem original.
function agruparPorSprint(tarefas, sprintAtivo) {
  const grupos = new Map();
  tarefas.forEach(t => {
    const chave = t.sprint || "sem-sprint";
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave).push(t);
  });
  const entradas = [...grupos.entries()].map(([sprint, tarefasGrupo]) => ({
    sprint,
    isAtiva: sprint === "sem-sprint" || sprint === sprintAtivo,
    tarefas: tarefasGrupo,
  }));
  return entradas.sort((a, b) => (b.isAtiva ? 1 : 0) - (a.isAtiva ? 1 : 0));
}

export default function Tempos() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);
  const [colapsados, setColapsados] = useState({});
  const [filtro, setFiltro] = useState(null); // null | true (Programado) | false (Emergência)
  const loadRef = useRef(null);

  async function load() {
    setLoading(true);
    setError(false);
    try {
      const res = await getTempos();
      setData(res);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  loadRef.current = load;
  useEffect(() => { load(); }, []);

  function toggleDev(nome) {
    setColapsados(prev => ({ ...prev, [nome]: !prev[nome] }));
  }

  if (loading) return (
    <div style={page}>
      <div style={{ color: "#475569", textAlign: "center", paddingTop: 60, fontSize: 13 }}>
        Carregando horas lançadas…
      </div>
    </div>
  );

  if (error) return (
    <div style={page}>
      <div style={{ color: "#f87171", textAlign: "center", paddingTop: 60, fontSize: 14 }}>
        Erro ao carregar dados.{" "}
        <button onClick={() => loadRef.current()} style={{ color: "#60a5fa", background: "none", border: "none", cursor: "pointer", fontSize: 14 }}>
          Tentar novamente
        </button>
      </div>
    </div>
  );

  const dias = data?.dias || [];
  const todosDevs = data?.desenvolvedores || [];
  const sprintAtiva = data?.periodo?.sprint;

  const ticketsUnicos = new Map();
  todosDevs.forEach(d => d.tarefas.forEach(t => {
    if (!ticketsUnicos.has(t.issue_id)) ticketsUnicos.set(t.issue_id, t);
  }));
  const totalTickets  = ticketsUnicos.size;
  // "Programado" só conta tarefas da sprint ativa — as de sprints antigas
  // viraram "Retrabalho" (ver agruparPorSprint) e não entram nessa contagem.
  const qtdProgramado = [...ticketsUnicos.values()].filter(t => t.programado === true && isTarefaSprintAtiva(t, sprintAtiva)).length;
  const qtdEmergencia = [...ticketsUnicos.values()].filter(t => t.programado === false).length;

  function toggleFiltro(valor) {
    setFiltro(prev => (prev === valor ? null : valor));
  }

  function tarefaCorrespondeAoFiltro(t) {
    if (filtro === null) return true;
    if (filtro === true) return t.programado === true && isTarefaSprintAtiva(t, sprintAtiva);
    return t.programado === false;
  }

  const devs = (filtro === null ? todosDevs : todosDevs
    .map(d => {
      const tarefas = d.tarefas.filter(tarefaCorrespondeAoFiltro);
      if (tarefas.length === 0) return null;
      const totais_dia = Object.fromEntries(dias.map(dia => [dia, 0]));
      let total = 0;
      tarefas.forEach(t => {
        total += t.total;
        dias.forEach(dia => { totais_dia[dia] += t.horas_dia[dia] || 0; });
      });
      return { ...d, tarefas, totais_dia, total: Math.round(total * 100) / 100 };
    })
    .filter(Boolean));

  const totaisDiaGeral = Object.fromEntries(dias.map(dia => [dia, 0]));
  let totalGeral = 0;
  devs.forEach(d => {
    totalGeral += d.total;
    dias.forEach(dia => { totaisDiaGeral[dia] += d.totais_dia[dia] || 0; });
  });

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div>
          <h1 style={h1}>Tempos — Horas Lançadas por Desenvolvedor</h1>
          <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>
            {data?.periodo
              ? `Sprint ${data.periodo.sprint} · ${fmtDataBR(data.periodo.data_inicial)} a ${fmtDataBR(data.periodo.data_final)}`
              : "Sprint em execução"}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            onClick={() => setFiltro(null)}
            style={{ ...kpiPill, ...kpiPillClicavel, borderColor: "rgba(255,255,255,0.12)", ...(filtro === null ? kpiPillAtivo("rgba(255,255,255,0.35)") : null) }}
          >
            <span style={{ color: "#e2e8f0", fontWeight: 800, fontSize: 16 }}>{totalTickets}</span>
            <span style={{ color: "#475569", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>tickets</span>
          </div>
          <div
            onClick={() => toggleFiltro(true)}
            style={{ ...kpiPill, ...kpiPillClicavel, borderColor: "rgba(83,74,183,0.35)", ...(filtro === true ? kpiPillAtivo("#AFA9EC") : null) }}
          >
            <span style={{ color: "#AFA9EC", fontWeight: 800, fontSize: 16 }}>{qtdProgramado}</span>
            <span style={{ color: "#475569", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>programado</span>
          </div>
          <div
            onClick={() => toggleFiltro(false)}
            style={{ ...kpiPill, ...kpiPillClicavel, borderColor: "rgba(226,75,74,0.35)", ...(filtro === false ? kpiPillAtivo("#F09595") : null) }}
          >
            <span style={{ color: "#F09595", fontWeight: 800, fontSize: 16 }}>{qtdEmergencia}</span>
            <span style={{ color: "#475569", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>emergência</span>
          </div>
          <div style={{ ...kpiPill, borderColor: "rgba(96,165,250,0.3)" }}>
            <span style={{ color: "#60a5fa", fontWeight: 800, fontSize: 16 }}>{fmtHoras(totalGeral)}</span>
            <span style={{ color: "#475569", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>total {filtro === null ? "geral" : "filtrado"}</span>
          </div>
          <button onClick={() => loadRef.current()} style={btnRefresh}>↻</button>
        </div>
      </header>

      {devs.length === 0 ? (
        <div style={{ color: "#334155", textAlign: "center", paddingTop: 40, fontSize: 13 }}>
          {filtro === null
            ? "Nenhum apontamento de horas encontrado no período da sprint."
            : "Nenhum ticket encontrado para este filtro."}
        </div>
      ) : (
        <div style={tableWrap}>
          <table style={table}>
            <colgroup>
              <col style={{ width: 460 }} />
              {dias.map(dia => <col key={dia} style={{ width: 58 }} />)}
              <col style={{ width: 72 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ ...th, ...thSticky, textAlign: "left" }}>Usuário / Tarefa</th>
                {dias.map(dia => {
                  const fds = isFimDeSemana(dia);
                  return (
                    <th key={dia} style={{ ...th, ...(fds ? thFimSemana : null) }}>
                      <div>{fmtDia(dia)}</div>
                      <div style={{ fontSize: 9, color: fds ? "rgba(251,191,36,0.65)" : "#94a3b8", fontWeight: 500, textTransform: "none" }}>{fmtDiaSemana(dia)}</div>
                    </th>
                  );
                })}
                <th style={{ ...th, ...thTotal }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {devs.map(dev => {
                const av = devAv(dev.usuario);
                const isColapsado = !!colapsados[dev.usuario];
                return (
                  <Fragment key={dev.usuario}>
                    <tr style={rowDev} onClick={() => toggleDev(dev.usuario)}>
                      <td style={{ ...td, ...tdSticky, ...tdStickyDev, cursor: "pointer" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 10, color: "#475569", width: 10, flexShrink: 0 }}>{isColapsado ? "▶" : "▼"}</span>
                          <div style={{ width: 22, height: 22, borderRadius: "50%", background: av.bg, color: av.color, fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {devInitials(dev.usuario)}
                          </div>
                          <span style={{ color: "#e2e8f0", fontWeight: 700, fontSize: 12.5 }}>{dev.usuario}</span>
                          <span style={{ color: "#94a3b8", fontWeight: 600, fontSize: 10, background: "rgba(255,255,255,0.08)", padding: "2px 8px", borderRadius: 99 }}>{dev.tarefas.length} tarefa{dev.tarefas.length !== 1 ? "s" : ""}</span>
                        </div>
                      </td>
                      {dias.map(dia => {
                        const horasDia = dev.totais_dia[dia];
                        const abaixoMeta = horasDia > 0 && horasDia < 6;
                        return (
                          <td
                            key={dia}
                            style={{
                              ...td,
                              color: abaixoMeta ? "#f87171" : "#94a3b8",
                              fontWeight: 700,
                              ...(isFimDeSemana(dia) ? tdFimSemana : null),
                            }}
                          >
                            {fmtHoras(horasDia)}
                          </td>
                        );
                      })}
                      <td style={{ ...td, ...tdTotal, color: "#60a5fa" }}>{fmtHoras(dev.total)}</td>
                    </tr>

                    {!isColapsado && agruparPorSprint(dev.tarefas, data?.periodo?.sprint).map(grupo => (
                      <Fragment key={grupo.sprint}>
                        <tr style={rowGrupoSprint}>
                          <td style={{ ...td, ...tdSticky, textAlign: "left", paddingLeft: 24, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: grupo.isAtiva ? "rgba(255,255,255,0.3)" : "#FBBF24" }}>
                            {grupo.sprint === "sem-sprint" ? "Sem sprint vinculada" : `Sprint ${grupo.sprint}`}
                            {!grupo.isAtiva && " — retrabalho"}
                          </td>
                          {dias.map(dia => <td key={dia} style={{ ...td, ...(isFimDeSemana(dia) ? tdFimSemana : null) }} />)}
                          <td style={{ ...td, ...tdTotal }} />
                        </tr>
                        {grupo.tarefas.map(t => {
                          const classeBadge = t.programado === true
                            ? (grupo.isAtiva
                                ? { label: "Programado", color: "#AFA9EC", bg: "rgba(83,74,183,0.18)" }
                                : { label: "Retrabalho", color: "#FBBF24", bg: "rgba(251,191,36,0.18)" })
                            : t.programado === false
                              ? { label: "Emergência", color: "#F09595", bg: "rgba(226,75,74,0.18)" }
                              : null;
                          return (
                          <tr
                            key={t.issue_id}
                            style={rowTarefa}
                            onDoubleClick={() => window.open(`https://redmine.lexsis.com.br/issues/${t.issue_id}`, "_blank")}
                          >
                            <td style={{ ...td, ...tdSticky, textAlign: "left", paddingLeft: 44, whiteSpace: "nowrap", cursor: "pointer" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                {classeBadge && (
                                  <span style={{ fontSize: 9, fontWeight: 700, color: classeBadge.color, background: classeBadge.bg, padding: "1px 6px", borderRadius: 99, flexShrink: 0 }}>
                                    {classeBadge.label}
                                  </span>
                                )}
                                <span
                                  title={`${t.status} · ${t.tracker} #${t.issue_id}: ${t.subject} — duplo clique para abrir no Redmine`}
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    color: t.fechado ? "#475569" : "#cbd5e1",
                                    textDecoration: t.fechado ? "line-through" : "none",
                                    fontSize: 12,
                                    minWidth: 0,
                                  }}
                                >
                                  {t.tracker} #{t.issue_id}: {t.subject}
                                </span>
                              </div>
                            </td>
                            {dias.map(dia => (
                              <td key={dia} style={{ ...td, color: "rgba(255,255,255,0.5)", ...(isFimDeSemana(dia) ? tdFimSemana : null) }}>
                                {fmtHoras(t.horas_dia[dia])}
                              </td>
                            ))}
                            <td style={{ ...td, ...tdTotal, color: "rgba(255,255,255,0.6)" }}>{fmtHoras(t.total)}</td>
                          </tr>
                          );
                        })}
                      </Fragment>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={rowGeral}>
                <td style={{ ...td, ...tdSticky, ...tdStickyDev, textAlign: "left", color: "#f1f5f9", fontWeight: 800 }}>Total Geral</td>
                {dias.map(dia => (
                  <td key={dia} style={{ ...td, color: "#f1f5f9", fontWeight: 800, ...(isFimDeSemana(dia) ? tdFimSemana : null) }}>{fmtHoras(totaisDiaGeral[dia])}</td>
                ))}
                <td style={{ ...td, ...tdTotal, color: "#60a5fa", fontWeight: 800 }}>{fmtHoras(totalGeral)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Estilos ──────────────────────────────────────────────────────── */

const page = { padding: "28px 32px", minHeight: "100vh", color: "#f1f5f9" };

const header = {
  display: "flex", alignItems: "flex-start",
  justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap",
};

const h1 = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: 0, letterSpacing: "-0.01em" };

const btnRefresh = {
  background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.15)",
  color: "#60a5fa", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 16,
};

const kpiPill = {
  display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1,
  background: "rgba(11,22,42,0.72)", border: "1px solid", borderRadius: 10,
  padding: "6px 14px",
};

const kpiPillClicavel = { cursor: "pointer", userSelect: "none" };

const kpiPillAtivo = (cor) => ({
  borderColor: cor,
  background: "rgba(255,255,255,0.06)",
  boxShadow: `0 0 0 1px ${cor} inset`,
});

const tableWrap = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
  overflow: "auto",
  maxHeight: "calc(100vh - 190px)",
};

const table = { borderCollapse: "collapse", width: "100%", minWidth: 640, tableLayout: "fixed" };

const th = {
  position: "sticky", top: 0, zIndex: 2,
  background: "#0b1729",
  color: "rgba(255,255,255,0.6)",
  fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
  padding: "10px 10px", textAlign: "center",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  whiteSpace: "nowrap",
};

const thSticky = { position: "sticky", left: 0, top: 0, zIndex: 3, width: 460 };
const thTotal  = { position: "sticky", right: 0, top: 0, zIndex: 3, background: "#0b1729" };

const td = {
  padding: "8px 10px", textAlign: "center", fontSize: 11.5,
  borderBottom: "1px solid rgba(255,255,255,0.04)",
  whiteSpace: "nowrap",
};

const tdSticky = {
  position: "sticky", left: 0, background: "#0d1a30", zIndex: 1,
  textAlign: "left", width: 460, overflow: "hidden",
};

const tdStickyDev = { background: "#101f3a" };

const tdTotal = {
  position: "sticky", right: 0, background: "#0d1a30", fontWeight: 700, zIndex: 1,
};

const rowDev         = { background: "rgba(96,165,250,0.06)" };
const rowTarefa      = {};
const rowGeral       = { background: "rgba(96,165,250,0.1)" };
const rowGrupoSprint = { background: "rgba(255,255,255,0.02)" };

const thFimSemana = { background: "#241a0f", color: "rgba(251,191,36,0.55)" };
const tdFimSemana = { background: "rgba(251,191,36,0.05)" };
